/**
 * ArtifactPanel - Minimalist artifact display panel
 *
 * Features:
 * - Tab navigation (Plots | Data | Analysis | Maneuvers)
 * - Collapsible panel
 * - Retro mission control aesthetic
 */

import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, LayoutPanelLeft } from 'lucide-react'
import type { Artifact } from '../../types/chat'
import PlotView from './PlotView'
import DataTable from './DataTable'
import ManeuverView from './ManeuverView'
import { extractCdmResults } from './cdmFormatters'
import SmartDataView from './SmartDataView'

interface ArtifactPanelProps {
  artifacts: Artifact[]
  onPlanManeuver?: (cdmId: string) => void
  disabled?: boolean
}

type TabType = 'plots' | 'data' | 'analysis' | 'maneuvers'

const TAB_STORAGE_KEY = 'artifact-panel-selected-tab'
const COLLAPSED_STORAGE_KEY = 'artifact-panel-collapsed'

function ArtifactPanel({ artifacts, onPlanManeuver, disabled = false }: ArtifactPanelProps) {
  const [selectedTab, setSelectedTab] = useState<TabType>(() => {
    const saved = localStorage.getItem(TAB_STORAGE_KEY)
    return (saved as TabType) || 'plots'
  })

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem(COLLAPSED_STORAGE_KEY)
    if (saved !== null) return saved === 'true'
    // No stored preference: default collapsed on small screens (the panel
    // overlays the chat there) and expanded on desktop.
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  })

  useEffect(() => {
    localStorage.setItem(TAB_STORAGE_KEY, selectedTab)
  }, [selectedTab])

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(isCollapsed))
  }, [isCollapsed])

  const filteredArtifacts = artifacts.filter((artifact) => {
    switch (selectedTab) {
      case 'plots':
        return artifact.type === 'plot'
      case 'data':
        return artifact.type === 'data'
      case 'analysis':
        return artifact.type === 'analysis'
      case 'maneuvers':
        return artifact.type === 'maneuver'
      default:
        return false
    }
  })

  const getArtifactCount = (tab: TabType): number => {
    return artifacts.filter((a) => {
      if (tab === 'plots') return a.type === 'plot'
      if (tab === 'data') return a.type === 'data'
      if (tab === 'analysis') return a.type === 'analysis'
      if (tab === 'maneuvers') return a.type === 'maneuver'
      return false
    }).length
  }

  const tabs: { id: TabType; label: string }[] = [
    { id: 'plots', label: 'Plots' },
    { id: 'data', label: 'Data' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'maneuvers', label: 'Maneuvers' },
  ]

  return (
    <>
      {/* Mobile-only floating button to reopen the panel when collapsed.
          On desktop the collapsed panel is a thin strip with its own chevron. */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="md:hidden fixed bottom-20 right-4 z-20 flex items-center gap-2 px-3 py-2 rounded-full border border-retro-amber bg-retro-bg text-retro-amber text-xs font-mono uppercase tracking-wider shadow-lg"
          title="Show artifacts"
        >
          <LayoutPanelLeft size={14} />
          Artifacts
          {artifacts.length > 0 && (
            <span className="px-1.5 rounded-full bg-retro-amber text-retro-bg">
              {artifacts.length}
            </span>
          )}
        </button>
      )}

      <div
        className={`bg-retro-surface flex flex-col transition-all duration-300 ${
          isCollapsed
            ? 'hidden md:flex md:w-10'
            : 'fixed inset-0 z-30 md:static md:z-auto md:flex-[2]'
        }`}
      >
      {/* Header with tabs */}
      <div className="border-b border-retro-border flex items-center px-2 py-2">
        {/* Collapse toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 rounded hover:bg-retro-bg transition-colors text-retro-dim hover:text-retro-muted"
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>

        {/* Tabs */}
        {!isCollapsed && (
          <div className="flex gap-1 ml-2">
            {tabs.map((tab) => {
              const count = getArtifactCount(tab.id)
              const isActive = selectedTab === tab.id

              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`
                    px-2 py-1 text-xs font-mono uppercase tracking-wider transition-colors rounded
                    ${
                      isActive
                        ? 'text-retro-cyan bg-retro-bg'
                        : 'text-retro-dim hover:text-retro-muted'
                    }
                  `}
                >
                  {tab.label}
                  {count > 0 && (
                    <span
                      className="ml-1.5 px-1.5 rounded text-xs"
                      style={{
                        backgroundColor: isActive ? '#66cccc' : '#333333',
                        color: isActive ? '#0d0d0d' : '#888888',
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto p-4">
          {selectedTab === 'plots' ? (
            <PlotView artifacts={filteredArtifacts} />
          ) : selectedTab === 'maneuvers' ? (
            <ManeuverView artifacts={filteredArtifacts} />
          ) : selectedTab === 'data' ? (
            filteredArtifacts.length === 0 ? (
              <EmptyState label="No data artifacts" />
            ) : (
              <div className="space-y-4">
                {filteredArtifacts.map((artifact, index) => {
                  // Extract CDM results from wrapper if present
                  const cdmResults = artifact.data
                    ? extractCdmResults(artifact.data as Record<string, unknown>)
                    : null

                  return (
                    <div key={index}>
                      {cdmResults ? (
                        <DataTable
                          data={cdmResults}
                          title={artifact.title}
                          onPlanManeuver={onPlanManeuver}
                          disabled={disabled}
                        />
                      ) : artifact.data && Array.isArray(artifact.data) ? (
                        <DataTable
                          data={artifact.data}
                          title={artifact.title}
                          onPlanManeuver={onPlanManeuver}
                          disabled={disabled}
                        />
                      ) : artifact.data && typeof artifact.data === 'object' ? (
                        <DataTable
                          data={[artifact.data as Record<string, unknown>]}
                          title={artifact.title}
                          onPlanManeuver={onPlanManeuver}
                          disabled={disabled}
                        />
                      ) : (
                        <ArtifactCard artifact={artifact} />
                      )}
                    </div>
                  )
                })}
              </div>
            )
          ) : selectedTab === 'analysis' ? (
            filteredArtifacts.length === 0 ? (
              <EmptyState label="No analysis artifacts" />
            ) : (
              <div className="space-y-4">
                {filteredArtifacts.map((artifact, index) => (
                  <div key={index}>
                    {artifact.data && typeof artifact.data === 'object' ? (
                      <SmartDataView data={artifact.data as Record<string, unknown>} title={artifact.title} />
                    ) : (
                      <ArtifactCard artifact={artifact} />
                    )}
                  </div>
                ))}
              </div>
            )
          ) : filteredArtifacts.length === 0 ? (
            <EmptyState label={`No ${selectedTab} artifacts`} />
          ) : (
            <div className="space-y-4">
              {filteredArtifacts.map((artifact, index) => (
                <ArtifactCard key={index} artifact={artifact} />
              ))}
            </div>
          )}
        </div>
      )}
      </div>
    </>
  )
}

// Empty state component
function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <p className="text-xs text-retro-dim">{label}</p>
        <p className="text-xs text-retro-subtle mt-1">Artifacts appear as agent generates them</p>
      </div>
    </div>
  )
}

// Generic artifact card
function ArtifactCard({ artifact }: { artifact: Artifact }) {
  return (
    <div
      className="rounded border p-3"
      style={{ backgroundColor: '#0d0d0d', borderColor: '#333333' }}
    >
      <h3 className="text-xs font-mono uppercase tracking-wider text-retro-amber mb-2">
        {artifact.title}
      </h3>
      {artifact.data_url ? (
        <a
          href={artifact.data_url}
          className="text-xs text-retro-cyan hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {artifact.data_url}
        </a>
      ) : artifact.data ? (
        <pre className="text-xs text-retro-muted font-mono overflow-x-auto max-h-40 p-2 rounded bg-retro-bg">
          {JSON.stringify(artifact.data, null, 2)}
        </pre>
      ) : (
        <span className="text-xs text-retro-dim italic">No data</span>
      )}
    </div>
  )
}

export default ArtifactPanel
